import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';

type CatalogType = 'activity' | 'restaurant' | 'lodging' | 'blog';

interface SanityImageAsset {
  url?: string;
  originalFilename?: string;
}

interface SanityImageEntry {
  asset?: SanityImageAsset;
  alt?: string;
}

interface ActivityPreviewQueryResult {
  name?: string;
  shortDescription?: string;
  area?: string;
  category?: string;
  images?: SanityImageEntry[];
}

interface RestaurantPreviewQueryResult {
  name?: string;
  shortDescription?: string;
  area?: string;
  serviceType?: string;
  priceRange?: string;
  cuisine?: string[];
  images?: SanityImageEntry[];
}

interface LodgingPreviewQueryResult {
  name?: string;
  shortDescription?: string;
  area?: string;
  priceRange?: string;
  lodgingType?: string;
  accommodationType?: string;
  images?: SanityImageEntry[];
}

interface BlogPreviewQueryResult {
  title?: string;
  metaTitle?: string;
  metaDescription?: string;
  excerpt?: string;
  seo?: {
    title?: string;
    description?: string;
  };
  mainImage?: SanityImageEntry;
}

export interface EntityPreviewDetails {
  title: string;
  summary?: string;
  imageUrl?: string;
  area?: string;
  serviceType?: string;
  priceRange?: string;
  cuisine?: string[];
  lodgingType?: string;
}

const ACTIVITY_PREVIEW_QUERY = `*[_type == "activity" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
  name,
  shortDescription,
  area,
  category,
  images[]{asset->{url, originalFilename}, alt}
}`;

const RESTAURANT_PREVIEW_QUERY = `*[_type == "restaurant" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
  name,
  shortDescription,
  area,
  serviceType,
  priceRange,
  cuisine,
  images[]{asset->{url, originalFilename}, alt}
}`;

const LODGING_PREVIEW_QUERY = `*[_type == "lodging" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
  name,
  shortDescription,
  area,
  priceRange,
  lodgingType,
  accommodationType,
  images[]{asset->{url, originalFilename}, alt}
}`;

const BLOG_PREVIEW_QUERY = `*[_type == "blogPost" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
  title,
  metaTitle,
  metaDescription,
  excerpt,
  seo {
    title,
    description
  },
  mainImage {
    asset->{url, originalFilename},
    alt
  }
}`;

function selectLeadImage(images: SanityImageEntry[] | undefined, slug: string): string | undefined {
  if (!Array.isArray(images) || images.length === 0) {
    return undefined;
  }

  const normalizedSlug = slug?.toLowerCase() ?? '';

  const slugMatched = images.find((image) => {
    const filename = image?.asset?.originalFilename?.toLowerCase();
    if (!filename) return false;
    return filename.startsWith(normalizedSlug);
  });

  const candidate = slugMatched || images[0];
  return candidate?.asset?.url;
}

export async function getEntityPreview(catalog: CatalogType, slug: string): Promise<EntityPreviewDetails | null> {
  if (!slug) return null;

  try {
    if (catalog === 'activity') {
      const data = (await sanityFetch(ACTIVITY_PREVIEW_QUERY, { slug })) as ActivityPreviewQueryResult | null;
      if (!data) return null;

      return {
        title: data.name || slug,
        summary: data.shortDescription || undefined,
        imageUrl: selectLeadImage(data.images, slug),
        area: data.area,
      };
    }

    if (catalog === 'lodging') {
      const data = (await sanityFetch(LODGING_PREVIEW_QUERY, { slug })) as LodgingPreviewQueryResult | null;
      if (!data) return null;

      return {
        title: data.name || slug,
        summary: data.shortDescription || undefined,
        imageUrl: selectLeadImage(data.images, slug),
        area: data.area,
        priceRange: data.priceRange,
        lodgingType: data.lodgingType || data.accommodationType,
      };
    }

    if (catalog === 'blog') {
      const data = (await sanityFetch(BLOG_PREVIEW_QUERY, { slug })) as BlogPreviewQueryResult | null;
      if (!data) return null;

      return {
        title: data.metaTitle || data.seo?.title || data.title || slug,
        summary: data.metaDescription || data.seo?.description || data.excerpt || undefined,
        imageUrl: data.mainImage?.asset?.url,
      };
    }

    const data = (await sanityFetch(RESTAURANT_PREVIEW_QUERY, { slug })) as RestaurantPreviewQueryResult | null;
    if (!data) return null;

    return {
      title: data.name || slug,
      summary: data.shortDescription || undefined,
      imageUrl: selectLeadImage(data.images, slug),
      area: data.area,
      serviceType: data.serviceType,
      priceRange: data.priceRange,
      cuisine: Array.isArray(data.cuisine) && data.cuisine.length ? data.cuisine : undefined,
    };
  } catch (error) {
    console.error('Failed to fetch entity preview from Sanity', { catalog, slug, error });
    return null;
  }
}
